import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  Divider,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  CircularProgress,
  Chip,
  Checkbox,
  Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InventoryIcon from '@mui/icons-material/Inventory';
import TimelineIcon from '@mui/icons-material/Timeline';
import WarningIcon from '@mui/icons-material/Warning';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BlockIcon from '@mui/icons-material/Block';
import moment from 'moment';
import ApprovedChip from '../../components/ApprovedChip';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import constants from '../../utilities/constants';
import RequestManager from '../../utilities/requestManager';
import AddServiceOrderDialog from '../../components/AddServiceOrderDialog';
import { useStackZIndex, ModalStackProvider, useSelectMenuProps } from '../../utilities/ModalStackContext';

// ── Per-service mechanic display helpers ─────────────────────────────────────
// Each service records the mechanic who actually did the work (service.mechanic
// / service.mechanicID) independently of the SO's queue assignment, so this
// history survives removing the SO from a mechanic's queue.
const MECH_AVATAR_COLORS = ['#1565C0', '#2E7D32', '#6A1B9A', '#C62828', '#00695C', '#E65100', '#4527A0', '#AD1457'];
const mechColor = (id) => {
  if (!id) return '#9e9e9e';
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return MECH_AVATAR_COLORS[h % MECH_AVATAR_COLORS.length];
};
const mechInitials = (name) => {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
};
// "Placeholder" is a stand-in mechanic in constants — never surface it as a real
// worker on the worked-on-by line or the per-service mechanic column.
const isPlaceholderMech = (name) => !name || name.trim().toLowerCase() === 'placeholder';
// The line's mechanic if they clock in (id first, legacy name fallback), else null.
// Clock-in mechanics are paid from their clocked hours, not the line's hours, so
// their submitted time is approved on submit with no manager review. New lines
// start on "Placeholder", so an unassigned line falls back to the signed-in mechanic.
const clockMechanicFor = (service) => {
  if (!service) return null;
  let m = service.mechanicID
    ? constants.mechanics.find(x => x.id === service.mechanicID)
    : constants.mechanics.find(x => x.name === service.mechanic);
  if (!m || isPlaceholderMech(m.name)) {
    m = constants.mechanics.find(x => x.id === StateManager.mechanicId);
  }
  return m && constants.isClockMechanic(m.id) ? m : null;
};

const ServiceTable = ({ services, title, theme, onServiceEdit, handleStatusChange, handleOpenSplitDialog, isAdmin, onSubmitTime, enableSelection = false, selectedIds, onToggleSelection, onToggleSelectAll }) => {
  const sorted = [...services].sort((a, b) => {
    const da = a.status_time ? moment(a.status_time, 'YYYY/MM/DD') : moment(0);
    const db = b.status_time ? moment(b.status_time, 'YYYY/MM/DD') : moment(0);
    return db.valueOf() - da.valueOf();
  });
  const selectableIds = sorted.filter(s => !s._estimating).map(s => s.id);
  const selectedCount = enableSelection ? selectableIds.filter(id => selectedIds.has(id)).length : 0;
  const allSelected = enableSelection && sorted.length > 0 && selectedCount === sorted.length;
  const someSelected = enableSelection && selectedCount > 0 && !allSelected;

  // Group rows by tag (first-seen order) so same-tag services cluster, matching
  // the estimate/print. Untagged services fall under "Other"; the subheader is
  // only shown once there's more than one tag group (so untagged lists look
  // unchanged). Within a group the status_time sort above is preserved.
  const totalCols = (enableSelection ? 1 : 0) + 6;
  const groups = [];
  const groupIdx = {};
  for (const s of sorted) {
    const tag = (s.tag && s.tag.trim()) || 'Other';
    if (groupIdx[tag] === undefined) { groupIdx[tag] = groups.length; groups.push({ tag, items: [] }); }
    groups[groupIdx[tag]].items.push(s);
  }
  const showGroupHeaders = groups.length > 1;

  const renderServiceRow = (service) => {
    // A provisional row (AI estimate still running) isn't editable or actionable.
    const estimating = !!service._estimating;
    const editable = onServiceEdit && !estimating && StateManager.canEditServiceLine(service);
    return (
    <TableRow
      key={service.id}
      hover
      onClick={() => editable && onServiceEdit(service)}
      sx={{
        cursor: editable ? 'pointer' : 'default',
        '&:hover': editable ? {
          bgcolor: alpha(theme.palette.primary.main, 0.1),
        } : {}
      }}
    >
      {enableSelection && (
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selectedIds.has(service.id)}
            onChange={() => onToggleSelection(service.id)}
            disabled={estimating}
          />
        </TableCell>
      )}
      <TableCell>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleStatusChange(service.id, service.status === 'complete' ? 'pending' : 'complete');
          }}
          disabled={estimating || (!isAdmin && service.status === 'complete')}
          sx={{
            color: service.status === 'complete' ? theme.palette.success.main : theme.palette.grey[500],
            '&:hover': {
              bgcolor: alpha(service.status === 'complete' ? theme.palette.success.main : theme.palette.grey[500], 0.1),
            },
          }}
        >
          {service.status === 'complete' ?
            <RadioButtonCheckedIcon /> :
            <RadioButtonUncheckedIcon />
          }
        </IconButton>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ textDecoration: service.declined ? 'line-through' : 'none', color: service.declined ? 'text.disabled' : 'inherit' }}>
            {service.name}
          </Typography>
          {service.declined && <Chip label="Declined" color="error" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
          <ApprovedChip line={service} />
        </Box>
      </TableCell>
      {/* Who actually worked this service — its own column. Retained even if the SO
          is removed from this mechanic's queue. Placeholder is hidden. */}
      <TableCell>
        {service.mechanic && !isPlaceholderMech(service.mechanic) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: mechColor(service.mechanicID), color: '#fff' }}>
              {mechInitials(service.mechanic)}
            </Avatar>
            <Typography variant="body2" color="text.secondary">{service.mechanic}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">—</Typography>
        )}
      </TableCell>
      <TableCell>
        {estimating ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="text.secondary">Estimating…</Typography>
          </Box>
        ) : (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <TimelineIcon sx={{ color: theme.palette.text.secondary }} />
          <Typography>{service.time || 0} hrs</Typography>
          {service.time_submission_status === 'pending' && (
            <Chip
              label={`${service.proposed_time} proposed`}
              size="small"
              color="warning"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {service.status_time
            ? moment(service.status_time, 'YYYY/MM/DD').format('MMM DD, YYYY')
            : '-'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Box display="flex" gap={1} justifyContent="flex-end">
          {!estimating && !StateManager.isBackoffice() && (
            <Tooltip title={
              service.time_submission_status === 'pending'
                ? 'Awaiting manager approval'
                : 'Submit time for approval'
            }>
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onSubmitTime(service);
                  }}
                  size="small"
                  disabled={service.time_submission_status === 'pending'}
                  sx={{
                    color: service.time_submission_status === 'pending'
                      ? theme.palette.warning.main
                      : theme.palette.info.main,
                  }}
                >
                  <AccessTimeIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!estimating && (
          <Tooltip title="Split Service Time">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenSplitDialog(service.id);
              }}
              size="small"
              sx={{ color: theme.palette.primary.main }}
            >
              <Divider sx={{ display: 'inline', width: 2, height: 20, bgcolor: theme.palette.primary.main, mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Split</Typography>
            </IconButton>
          </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
    );
  };

  return (
    <>
      <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
        {title} ({services.length})
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {enableSelection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={() => onToggleSelectAll(selectableIds, allSelected)}
                  />
                </TableCell>
              )}
              <TableCell>Status</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Mechanic</TableCell>
              <TableCell>Hours</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <React.Fragment key={group.tag}>
                {showGroupHeaders && (
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={totalCols} sx={{ fontWeight: 'bold', py: 1 }}>
                      {group.tag}
                    </TableCell>
                  </TableRow>
                )}
                {group.items.map(renderServiceRow)}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const ServicesBlade = ({ order, onClose, onUpdate, onServiceEdit }) => {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [services, setServices] = useState(order.services || []);
  // Latest services list for async reconciliation (avoids stale closures when a
  // background create resolves after other state has moved on).
  const servicesRef = useRef(services);
  useEffect(() => { servicesRef.current = services; }, [services]);
  const [mechanicDialogOpen, setMechanicDialogOpen] = useState(false);
  const [selectedMechanicId, setSelectedMechanicId] = useState('');
  const [pendingServiceIds, setPendingServiceIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const isAdmin = StateManager.isAdmin();

  // --- STATE for submit time dialog ---
  const [submitTimeDialogOpen, setSubmitTimeDialogOpen] = useState(false);
  const [submitTimeService, setSubmitTimeService] = useState(null);
  const [submitTimeHours, setSubmitTimeHours] = useState('');
  const autoApproveTime = !!clockMechanicFor(submitTimeService);
  const [submitTimeError, setSubmitTimeError] = useState('');
  const [submitTimeLoading, setSubmitTimeLoading] = useState(false);

  // --- STATE for split dialog ---
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitServiceId, setSplitServiceId] = useState(null);
  const [splitHours, setSplitHours] = useState('');
  const [splitError, setSplitError] = useState('');
  const [splitLoading, setSplitLoading] = useState(false);
  // --- STATE for actions menu ---
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [actionsServiceId, setActionsServiceId] = useState(null);

  // --- STATE for AddServiceOrderDialog ---
  const [addServiceDialogOpen, setAddServiceDialogOpen] = useState(false);

  // Keep services state in sync with order prop
  useEffect(() => {
    if (order && order.services) {
      setServices(order.services);
    }
  }, [order?.services]);

  // Prune selectedIds if the underlying service no longer exists or is now complete
  useEffect(() => {
    setSelectedIds(prev => {
      if (!prev.size) return prev;
      const next = new Set();
      services.forEach(s => {
        if (prev.has(s.id) && s.status !== 'complete' && !s.declined) next.add(s.id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [services]);

  const handleServiceUpdate = async (serviceId, updates) => {
    console.log('handleServiceUpdate', serviceId, updates);
    try {
      await firebase.firestore().collection('services').doc(serviceId).update(updates);
      // Update local state
      const updatedServices = services.map(service =>
        service.id === serviceId ? { ...service, ...updates } : service
      );
      setServices(updatedServices);
      onUpdate(updatedServices);
    } catch (error) {
      console.error('Error updating service:', error);
      StateManager.setAlertAndOpen('Error updating service', 'error');
    }
  };

  const completeUpdateFor = (service, mechanicID) => {
    const mechanic = constants.mechanics.find(m => m.id === mechanicID)?.name;
    const status_time = moment().format('YYYY/MM/DD');
    const rate = constants.rateOn(mechanicID, status_time);
    const cost = (rate || 0) * (service?.time || 0);
    return {
      status: 'complete',
      status_time,
      mechanic,
      mechanicID,
      rate,
      cost,
    };
  };

  const handleBulkServiceUpdate = async (ids, updateForService) => {
    if (!ids.length) return;
    try {
      const batch = firebase.firestore().batch();
      const updatesById = {};
      ids.forEach(id => {
        const service = services.find(s => s.id === id);
        if (!service) return;
        const updates = updateForService(service);
        updatesById[id] = updates;
        batch.update(firebase.firestore().collection('services').doc(id), updates);
      });
      await batch.commit();
      const updatedServices = services.map(s =>
        updatesById[s.id] ? { ...s, ...updatesById[s.id] } : s
      );
      setServices(updatedServices);
      onUpdate(updatedServices);
    } catch (error) {
      console.error('Error bulk updating services:', error);
      StateManager.setAlertAndOpen('Error updating services', 'error');
    }
  };

  const handleStatusChange = (serviceId, newStatus) => {
    if (newStatus === 'complete') {
      // Only ask for mechanic selection if user is admin
      if (StateManager.isAdmin()) {
        setPendingServiceIds([serviceId]);
        setMechanicDialogOpen(true);
      } else {
        // Non-admin users: auto-use their mechanicId
        const mechanicID = StateManager.mechanicId;
        const service = services.find(s => s.id === serviceId);
        handleServiceUpdate(serviceId, completeUpdateFor(service, mechanicID));
      }
    } else {
      // For marking as pending, use the order's mechanic
      const mechanicID = order.mechanicId;
      const mechanic = constants.mechanics.find(m => m.id === mechanicID)?.name;
      const status_time = moment().format('YYYY/MM/DD');
      const rate = constants.rateOn(mechanicID, status_time);
      const cost = rate * (services.find(s => s.id === serviceId).time || 0);
      handleServiceUpdate(serviceId, { status: newStatus, status_time, mechanic, mechanicID, rate, cost });
    }
  };

  const handleToggleSelection = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = (ids, allCurrentlySelected) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allCurrentlySelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleBulkDecline = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Decline ${ids.length} service${ids.length > 1 ? 's' : ''}? They'll be excluded from accounting.`)) return;
    await handleBulkServiceUpdate(ids, () => ({ declined: true }));
    setSelectedIds(new Set());
    StateManager.setAlertAndOpen(`Declined ${ids.length} service${ids.length > 1 ? 's' : ''} — excluded from accounting`, 'warning');
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} service${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      const batch = firebase.firestore().batch();
      ids.forEach(id => batch.delete(firebase.firestore().collection('services').doc(id)));
      await batch.commit();

      const updatedServices = services.filter(s => !ids.includes(s.id));
      setServices(updatedServices);
      onUpdate(updatedServices);

      // Keep the order's estimate/invoice selection in sync.
      const currentSelected = order.selectedServiceIds || services.map(s => s.id);
      const newSelected = currentSelected.filter(id => !ids.includes(id));
      await firebase.firestore().doc(`orders/${order.id}`).update({ selectedServiceIds: newSelected });
      StateManager.updateOrder({ selectedServiceIds: newSelected });

      setSelectedIds(new Set());
      StateManager.setAlertAndOpen(`Deleted ${ids.length} service${ids.length > 1 ? 's' : ''}`, 'success');
    } catch (error) {
      console.error('Error bulk deleting services:', error);
      StateManager.setAlertAndOpen('Error deleting services', 'error');
    }
  };

  const handleBulkMarkComplete = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (StateManager.isAdmin()) {
      setPendingServiceIds(ids);
      setMechanicDialogOpen(true);
    } else {
      const mechanicID = StateManager.mechanicId;
      handleBulkServiceUpdate(ids, (service) => completeUpdateFor(service, mechanicID));
      setSelectedIds(new Set());
    }
  };

  const handleMechanicConfirm = async () => {
    if (!selectedMechanicId || !pendingServiceIds.length) return;
    if (pendingServiceIds.length === 1) {
      const service = services.find(s => s.id === pendingServiceIds[0]);
      await handleServiceUpdate(pendingServiceIds[0], completeUpdateFor(service, selectedMechanicId));
    } else {
      await handleBulkServiceUpdate(pendingServiceIds, (service) => completeUpdateFor(service, selectedMechanicId));
      setSelectedIds(new Set());
    }
    setMechanicDialogOpen(false);
    setSelectedMechanicId('');
    setPendingServiceIds([]);
  };

  const handleMechanicCancel = () => {
    setMechanicDialogOpen(false);
    setSelectedMechanicId('');
    setPendingServiceIds([]);
  };

  // Merge services into the current list by id (idempotent with the SO modal's
  // realtime snapshot). Optionally drop a provisional row that's being replaced.
  const mergeServices = (incoming, dropId = null) => {
    const base = servicesRef.current.filter(s => s.id !== dropId);
    const byId = new Map(base.map(s => [s.id, s]));
    incoming.forEach(s => byId.set(s.id, { ...byId.get(s.id), ...s }));
    const merged = Array.from(byId.values());
    setServices(merged);
    onUpdate(merged);
  };

  // Adding a service is optimistic: the dialog hands off a request descriptor
  // and closes immediately. We drop in a provisional "Estimating…" row right
  // away, run createServices (AI book-time estimate) in the background, then
  // reconcile the real rows in — or roll the provisional row back on failure.
  const handleAddService = async (request) => {
    // Back-compat: a plain array of already-created services just merges in.
    if (Array.isArray(request)) {
      mergeServices(request);
      return;
    }

    const { description, removeTime, includeInEstimate, estimateParts } = request || {};
    if (!description) return;

    const tempId = `pending-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
    const today = moment().format('YYYY/MM/DD');
    const provisional = {
      id: tempId,
      name: description,
      time: 0,
      cost: 0,
      status: 'pending',
      tag: 'Diagnosis',
      order: order.id,
      date: today,
      status_time: today,
      _estimating: true,
    };
    mergeServices([provisional]);

    try {
      const response = await RequestManager.post({
        function: 'createServices',
        variables: {
          serviceOrderId: order.id,
          description,
          removeTime,
          includeInEstimate,
          estimateParts,
        },
      });
      if (!response.success) throw new Error(response.error || 'Failed to add service');
      mergeServices(response.services || [], tempId);
      StateManager.setAlertAndOpen('Service added successfully', 'success');
    } catch (error) {
      console.error('Error adding service:', error);
      mergeServices([], tempId); // roll back the provisional row
      StateManager.setAlertAndOpen(error.message || 'Error adding service', 'error');
    }
  };

  const handleOpenSubmitTimeDialog = (service) => {
    setSubmitTimeService(service);
    setSubmitTimeHours(String(service.time || ''));
    setSubmitTimeError('');
    setSubmitTimeDialogOpen(true);
  };

  const handleCloseSubmitTimeDialog = () => {
    setSubmitTimeDialogOpen(false);
    setSubmitTimeService(null);
    setSubmitTimeHours('');
    setSubmitTimeError('');
  };

  const handleConfirmSubmitTime = async () => {
    const hours = parseFloat(submitTimeHours);
    if (isNaN(hours) || hours < 0) {
      setSubmitTimeError('Please enter a valid number of hours.');
      return;
    }
    setSubmitTimeLoading(true);
    const now = moment().format('YYYY/MM/DD HH:mm');
    const logEntry = {
      action: 'submitted',
      proposed_time: hours,
      by: StateManager.mechanicName || StateManager.userName,
      userId: StateManager.mechanicId || StateManager.userID,
      at: now,
    };
    const log = [...(submitTimeService.time_submission_log || []), logEntry];
    // Flat-rate mechanics still go through the Proposed Hours queue.
    const clockMechanic = clockMechanicFor(submitTimeService);
    if (clockMechanic) {
      const rate = constants.rateOn(clockMechanic.id, submitTimeService.status_time || undefined);
      await handleServiceUpdate(submitTimeService.id, {
        time: hours,
        rate,
        cost: rate * hours,
        proposed_time: null,
        time_submission_status: 'approved',
        time_submission_log: [...log, { ...logEntry, action: 'auto-approved' }],
      });
      StateManager.setAlertAndOpen('Time saved', 'success');
    } else {
      await handleServiceUpdate(submitTimeService.id, {
        proposed_time: hours,
        time_submission_status: 'pending',
        time_submission_log: log,
      });
      StateManager.setAlertAndOpen('Time submitted for approval', 'success');
    }
    setSubmitTimeLoading(false);
    handleCloseSubmitTimeDialog();
  };

  // --- SPLIT LOGIC ---
  const handleOpenSplitDialog = (serviceId) => {
    setSplitServiceId(serviceId);
    setSplitHours('');
    setSplitError('');
    setSplitDialogOpen(true);
  };

  const handleCloseSplitDialog = () => {
    setSplitDialogOpen(false);
    setSplitServiceId(null);
    setSplitHours('');
    setSplitError('');
  };

  const handleConfirmSplit = async () => {
    const service = services.find(s => s.id === splitServiceId);
    if (!service) return;
    const originalHours = parseFloat(service.time) || 0;
    const hoursToSplit = parseFloat(splitHours);
    if (isNaN(hoursToSplit) || hoursToSplit <= 0) {
      setSplitError('Please enter a valid number of hours.');
      return;
    }
    if (hoursToSplit >= originalHours) {
      setSplitError('Split hours must be less than the original hours.');
      return;
    }
    setSplitLoading(true);
    // Find mechanic/rate for cost calculation
    const mechanicID = service.mechanicID || order.mechanicId;
    // Both halves keep the original status_time, so both take its day's rate.
    const rate = constants.rateOn(mechanicID, service.status_time || undefined);
    // Find next part number for this service name
    const baseName = service.name.replace(/ - part \d+$/, '');
    const siblings = services.filter(s => s.name.startsWith(baseName));
    let maxPart = 1;
    siblings.forEach(s => {
      const match = s.name.match(/ - part (\d+)$/);
      if (match) {
        maxPart = Math.max(maxPart, parseInt(match[1], 10));
      }
    });
    const newPartNum = maxPart + 1;
    // Prepare new service object
    const docRef = await firebase.firestore().collection('services').doc();
    const newService = {
      ...service,
      name: `${baseName} - part ${newPartNum}`,
      time: hoursToSplit,
      rate,
      cost: rate * hoursToSplit,
      created_at: moment().format('YYYY/MM/DD'),
      status: 'pending',
      id: docRef.id,
    };
    // Update original service
    const updatedOriginal = {
      ...service,
      time: originalHours - hoursToSplit,
      rate,
      cost: rate * (originalHours - hoursToSplit),
    };
    try {
      // Add new service to Firestore
      await docRef.set({
        ...newService,
        order: order.id,
      });
      // Update original service in Firestore
      await firebase.firestore().collection('services').doc(service.id).update({
        time: updatedOriginal.time,
        cost: updatedOriginal.cost,
      });
      // Update local state (show new service immediately)
      const updatedServices = services
        .map(s => s.id === service.id ? { ...updatedOriginal } : s)
        .concat([newService]);
      setServices(updatedServices);
      onUpdate(updatedServices);
      setSplitLoading(false);
      handleCloseSplitDialog();
    } catch (error) {
      console.error('Error splitting service:', error);
      StateManager.setAlertAndOpen('Error splitting service', 'error');
      setSplitLoading(false);
    }
  };

  // --- ACTIONS MENU LOGIC ---
  const handleOpenActionsMenu = (event, serviceId) => {
    setActionsAnchorEl(event.currentTarget);
    setActionsServiceId(serviceId);
  };
  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null);
    setActionsServiceId(null);
  };
  const handleMenuSplit = () => {
    handleOpenSplitDialog(actionsServiceId);
    handleCloseActionsMenu();
  };

  const filteredServices = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return services.filter(service =>
      service.name.toLowerCase().includes(searchLower) ||
      (service.mechanic || '').toLowerCase().includes(searchLower)
    );
  }, [services, searchQuery]);

  // Distinct mechanics who have worked any service on this order (from the
  // per-service record, not the SO's current queue assignment).
  const workedBy = useMemo(() => {
    const map = new Map();
    services.forEach(s => {
      if (s.mechanic && s.mechanicID && !isPlaceholderMech(s.mechanic) && !map.has(s.mechanicID)) map.set(s.mechanicID, s.mechanic);
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [services]);

  // Declined services are grouped with completed work (and shown as declined),
  // not left in the active/pending list.
  const pendingServices = filteredServices.filter(service => service.status !== 'complete' && !service.declined);
  const completedServices = filteredServices.filter(service => service.status === 'complete' || service.declined);

  // Calculate totals
  const totalHours = services.reduce((sum, service) => sum + (parseFloat(service.time) || 0), 0);
  const totalCost = services.reduce((sum, service) => sum + (parseFloat(service.cost) || 0), 0);

  return (
    <ModalStackProvider value={myZ}>
    <Box sx={{ p: 2 }}>
      {/* Who has worked on this car (across all its services) */}
      {workedBy.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>Worked on by:</Typography>
          {workedBy.map(m => (
            <Chip
              key={m.id}
              size="small"
              avatar={<Avatar sx={{ bgcolor: mechColor(m.id), color: '#fff !important' }}>{mechInitials(m.name)}</Avatar>}
              label={m.name}
              variant="outlined"
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search services..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddServiceDialogOpen(true)}
          sx={{ whiteSpace: 'nowrap' }}
          >
            Add Services
          </Button>
      </Box>

      {/* Total Hours and Cost Summary */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          mb: 2,
          px: 2,
          py: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Total Hours:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {totalHours.toFixed(2)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCartIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Total Cost:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </Box>
      </Box>
      {selectedIds.size > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            px: 2,
            py: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selectedIds.size} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={handleBulkMarkComplete}
          >
            Mark Complete
          </Button>
          {StateManager.isBackoffice() && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<BlockIcon />}
              onClick={handleBulkDecline}
            >
              Decline Selected
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
            >
              Delete Selected
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearSelection}
          >
            Clear
          </Button>
        </Box>
      )}
      {filteredServices.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} sx={{ py: 4 }}>
          <WarningIcon sx={{ fontSize: 48, color: theme.palette.warning.main }} />
          <Typography variant="h6" color="text.secondary">
            No services found
          </Typography>
        </Box>
      ) : (
        <>
          {pendingServices.length > 0 && (
            <ServiceTable
              services={pendingServices}
              title="Pending Services"
              theme={theme}
              onServiceEdit={onServiceEdit}
              handleStatusChange={handleStatusChange}
              handleOpenSplitDialog={handleOpenSplitDialog}
              isAdmin={isAdmin}
              onSubmitTime={handleOpenSubmitTimeDialog}
              enableSelection
              selectedIds={selectedIds}
              onToggleSelection={handleToggleSelection}
              onToggleSelectAll={handleToggleSelectAll}
            />
          )}
          {completedServices.length > 0 && (
            <>
              {pendingServices.length > 0 && <Divider sx={{ my: 2 }} />}
              <ServiceTable
                services={completedServices}
                title="Completed Services"
                theme={theme}
                onServiceEdit={onServiceEdit}
                handleStatusChange={handleStatusChange}
                handleOpenSplitDialog={handleOpenSplitDialog}
                isAdmin={isAdmin}
                onSubmitTime={handleOpenSubmitTimeDialog}
              />
            </>
          )}
        </>
      )}

      {/* Mechanic Selection Dialog */}
      <Dialog open={mechanicDialogOpen} onClose={handleMechanicCancel} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
        <DialogTitle>
          {pendingServiceIds.length > 1
            ? `Select Mechanic for ${pendingServiceIds.length} Services`
            : 'Select Mechanic'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pendingServiceIds.length > 1
              ? `Please select the mechanic who completed these ${pendingServiceIds.length} services:`
              : 'Please select the mechanic who completed this service:'}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Mechanic</InputLabel>
            <Select
              value={selectedMechanicId}
              onChange={(e) => setSelectedMechanicId(e.target.value)}
              label="Mechanic"
              MenuProps={selectMenuProps}
            >
              {constants.mechanics
                .filter(mechanic => mechanic.name !== "Placeholder" && mechanic.is_active)
                .map((mechanic) => (
                  <MenuItem key={mechanic.id} value={mechanic.id}>
                    {mechanic.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMechanicCancel}>Cancel</Button>
          <Button
            onClick={handleMechanicConfirm}
            variant="contained"
            disabled={!selectedMechanicId}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* Split Service Dialog */}
      <Dialog open={splitDialogOpen} onClose={handleCloseSplitDialog} maxWidth="xs" fullWidth sx={{ zIndex: myZ }}>
        <DialogTitle>Split Service Time</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the number of hours to split off from this service:
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Hours to split"
            type="number"
            fullWidth
            value={splitHours}
            onChange={e => setSplitHours(e.target.value)}
            error={!!splitError}
            helperText={splitError}
            inputProps={{ min: 0, step: 0.1 }}
            disabled={splitLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSplitDialog} disabled={splitLoading}>Cancel</Button>
          <Button onClick={handleConfirmSplit} variant="contained" disabled={splitLoading}>
            {splitLoading ? <CircularProgress size={24} color="inherit" /> : 'Split'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit Time Dialog */}
      <Dialog open={submitTimeDialogOpen} onClose={handleCloseSubmitTimeDialog} maxWidth="xs" fullWidth sx={{ zIndex: myZ }}>
        <DialogTitle>{autoApproveTime ? 'Submit Time' : 'Submit Time for Approval'}</DialogTitle>
        <DialogContent>
          {submitTimeService && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>{submitTimeService.name}</strong> — {autoApproveTime
                ? 'enter the hours for this line. They save right away.'
                : "enter the hours you're submitting for manager approval."}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Hours"
            type="number"
            fullWidth
            value={submitTimeHours}
            onChange={e => { setSubmitTimeHours(e.target.value); setSubmitTimeError(''); }}
            error={!!submitTimeError}
            helperText={submitTimeError}
            inputProps={{ min: 0, step: 0.25, autoComplete: 'off' }}
            disabled={submitTimeLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubmitTimeDialog} disabled={submitTimeLoading}>Cancel</Button>
          <Button onClick={handleConfirmSubmitTime} variant="contained" color="warning" disabled={submitTimeLoading}>
            {submitTimeLoading ? <CircularProgress size={20} color="inherit" /> : (autoApproveTime ? 'Save Time' : 'Submit for Approval')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Service Dialog */}
      <AddServiceOrderDialog
        open={addServiceDialogOpen}
        onClose={() => setAddServiceDialogOpen(false)}
        serviceOrderId={order?.id}
        callback={handleAddService}
      />
    </Box>
    </ModalStackProvider>
  );
};

export default ServicesBlade;
